/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { parseInt } from 'lodash';
import Button from 'components/Button';
import InputText from 'components/FormInputText';
import InputSelect from 'components/FormInputSelect';
import DatePicker from 'components/FormDatePicker';
import actions from 'redux/actions';
import { getIsLoading as getIsAuthLoading } from 'redux/reducers/auth';
import { getIsLoading as getIsRequestLoading } from 'redux/reducers/onDemand';
import protones from 'utils/protones';
import common from 'utils/common';
import validation from './validation';
import styles from './styles.module.scss';

import type { Translate, ReduxState, OnDemand, WorklistItem } from 'types';

export const requestTypes = {
  track: 1,
  quotation: 2,
  smartlist: 3,
  help: 4,
};

type FormValues = {
  title: string,
  date: number,
  note: string,
  trackTitle: string,
  trackPerformers: string,
  trackAuthors: string,
  quotationTitle: string,
  smartlistTitle: string,
  worklistItems?: WorklistItem[],
};

type Props = {
  isLoading: boolean,
  valid: boolean,
  pristine: boolean,
  dirty: boolean,
  selectedRequestType?: number,
  handleSubmit: (callback: mixed) => any,
  t: Translate,
  onSubmit: (request: OnDemand) => any,
};

const selector = formValueSelector('onDemandRequests');

const mapStateToProps = (state: ReduxState) => ({
  isLoading: getIsAuthLoading(state) || getIsRequestLoading(state),
  selectedRequestType: common.toNumber(selector(state, 'requestType')),
});

class OnDemandRequestsForm extends React.Component<Props, void> {
  _renderOptions = (): React$Element<any>[] => {
    const { t } = this.props;
    const options = [
      { id: requestTypes.track, name: t('TRACK') },
      { id: requestTypes.quotation, name: t('QUOTATION') },
      { id: requestTypes.smartlist, name: t('SMARTLIST') },
      { id: requestTypes.help, name: t('HELP') },
    ];
    if (options.length) {
      return [
        <option key={'null'} value={0}>
          {t('SELECT_A_VALUE')}
        </option>,
      ].concat(
        options.map(({ id, name }) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))
      );
    } else {
      return [
        <option key={'_'} value={0}>
          {t('NO_VALUE_AVAILABLE')}
        </option>,
      ];
    }
  };

  _handleSubmitClick = ({
    title = '',
    date = 0,
    note = '',
    trackTitle = '',
    trackPerformers = '',
    trackAuthors = '',
    quotationTitle = '',
    smartlistTitle = '',
    worklistItems,
  }: FormValues) => {
    const { selectedRequestType, onSubmit } = this.props;
    if (
      typeof selectedRequestType !== 'number' ||
      selectedRequestType <= 0 ||
      isNaN(selectedRequestType)
    ) {
      return;
    }
    switch (selectedRequestType) {
      case requestTypes.track:
        const trackRequest: OnDemand = {
          id: 0,
          type: 'TRACKS',
          title,
          date,
          note,
          track: {
            title: trackTitle,
            performers: trackPerformers,
            authors: trackAuthors,
          },
        };
        return onSubmit(trackRequest);
      case requestTypes.quotation:
        if (worklistItems != null) {
          const quotationRequest: OnDemand = {
            id: 0,
            type: 'QUOTATION',
            title,
            date,
            note,
            quotation: {
              title: quotationTitle,
              worklistItems,
            },
          };
          return onSubmit(quotationRequest);
        } else {
          break;
        }
      case requestTypes.smartlist:
        const smartlistRequest: OnDemand = {
          id: 0,
          type: 'SMARTLIST',
          title,
          date,
          note,
          smartlist: {
            title: smartlistTitle,
          },
        };
        return onSubmit(smartlistRequest);
      case requestTypes.help:
        const helpRequest: OnDemand = {
          id: 0,
          type: 'HELP',
          title,
          date,
          note,
        };
        return onSubmit(helpRequest);
      default:
        return;
    }
  };

  render() {
    const { handleSubmit, isLoading, valid, dirty, pristine, selectedRequestType, t } = this.props;
    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <Prompt when={dirty} message={location => t('CONFIRM_EXIT_DIRTY_FORM')} />
          <Field
            component={InputText}
            name={'title'}
            label={t('REQUEST_TITLE')}
            disabled={isLoading}
          />
          <Field
            component={InputText}
            name={'note'}
            label={t('NOTES')}
            disabled={isLoading}
            multiline={true}
            rows={3}
          />
          <Field
            component={DatePicker}
            name={'date'}
            label={t('DATE')}
            parse={protones.parseDateMillis}
            disabled={isLoading || selectedRequestType === requestTypes.quotation}
          />
          {!selectedRequestType && (
            <Field
              component={InputSelect}
              label={t('ONDEMAND_TYPE')}
              name={'requestType'}
              parse={parseInt}
              disabled={true}
            >
              {this._renderOptions()}
            </Field>
          )}
          {selectedRequestType === requestTypes.track && (
            <React.Fragment>
              <Field
                component={InputText}
                name={'trackTitle'}
                label={t('TRACK_TITLE')}
                disabled={isLoading}
              />
              <Field
                component={InputText}
                name={'trackPerformers'}
                label={t('PERFORMERS')}
                disabled={isLoading}
              />
              <Field
                component={InputText}
                name={'trackAuthors'}
                label={t('AUTHORS')}
                disabled={isLoading}
              />
            </React.Fragment>
          )}
          {selectedRequestType === requestTypes.quotation && (
            <React.Fragment>
              <Field
                component={InputText}
                name={'quotationTitle'}
                label={t('QUOTATION_TITLE')}
                disabled={true}
                multiline={true}
                rows={10}
              />
            </React.Fragment>
          )}
          {selectedRequestType === requestTypes.smartlist && (
            <React.Fragment>
              <Field
                component={InputText}
                name={'smartlistTitle'}
                label={t('SMARTLIST_TITLE')}
                disabled={isLoading}
              />
            </React.Fragment>
          )}
          <div className={styles.formButton}>
            <span />
            <Button
              text={t('SUBMIT')}
              onClick={handleSubmit(this._handleSubmitClick)}
              disabled={isLoading || !valid || pristine}
              loading={isLoading}
              appearance={'primary'}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(
  connect(
    mapStateToProps,
    actions
  )(
    reduxForm({
      form: 'onDemandRequests',
      validate: validation,
      enableReinitialize: true,
      //$FlowFixMe
      destroyOnUnmount: true,
    })(OnDemandRequestsForm)
  )
);
